/** The caller supplies ONLY new, unread alerts after establishing a silent feed baseline. */
export interface NotificationSound {
  enabled(): boolean;
  setEnabled(value: boolean): Promise<boolean>;
  test(): Promise<boolean>;
  notify(ids: string[], urgent: boolean): Promise<boolean>;
  dispose(): void;
  isReady(): boolean;
}

const COOLDOWN_MS = 8_000;
const SEEN_TTL_MS = 24 * 60 * 60 * 1_000;
const MAX_SEEN = 500;
const PEAK_GAIN = 0.15;
type SeenEntry = [string, number];
type SharedGuard = { lastPlayedAt: number; seen: SeenEntry[]; owner: string };

// Namespaces and alert IDs are opaque identifiers, never email addresses or alert text.
// Hashing keeps those identifiers out of localStorage; this is partitioning, not encryption.
function keyHash(value: string): string {
  let a = 0x811c9dc5, b = 0x9e3779b9;
  for (let index = 0; index < value.length; index++) {
    a = Math.imul(a ^ value.charCodeAt(index), 0x01000193);
    b = Math.imul(b ^ value.charCodeAt(index), 0x85ebca6b);
  }
  return (a >>> 0).toString(16).padStart(8, '0') + (b >>> 0).toString(16).padStart(8, '0');
}

export function createNotificationSound(scopeKey: string): NotificationSound {
  const browser = typeof window === 'undefined' ? undefined : window;
  const scope = keyHash(scopeKey);
  const preferenceKey = `restaurant-notification-sound:v1:${scope}`;
  const guardKey = `restaurant-notification-sound-guard:v1:${scope}`;
  const lockKey = `restaurant-notification-sound:v1:${scope}`;
  const owner = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  let optedIn = false;
  let disposed = false;
  let context: AudioContext | undefined;
  let generation = 0;
  let lastPlayedAt = 0;
  let unlocking: Promise<boolean> | undefined;
  const seen = new Map<string, number>();
  const sounding = new Set<{ oscillator: OscillatorNode; gain: GainNode }>();

  function storage(): Storage | undefined {
    try { return browser?.localStorage; } catch { return undefined; }
  }
  function readPreference(): boolean {
    try { return storage()?.getItem(preferenceKey) === 'true'; } catch { return false; }
  }
  function writePreference(): void {
    try { storage()?.setItem(preferenceKey, String(optedIn)); } catch { /* Session-only opt-in. */ }
  }
  function focused(): boolean {
    return !!browser && browser.document.visibilityState === 'visible' && browser.document.hasFocus();
  }
  function activeGesture(): boolean {
    // Older browsers expose no userActivation API; these methods must still be called by UI gestures.
    return !!browser && (!browser.navigator.userActivation || browser.navigator.userActivation.isActive);
  }
  function ready(): boolean { return !disposed && context?.state === 'running'; }
  function stopTones(): void {
    for (const node of sounding) {
      try { node.oscillator.stop(); } catch { /* Already ended. */ }
      try { node.oscillator.disconnect(); node.gain.disconnect(); } catch { /* Already detached. */ }
    }
    sounding.clear();
  }
  function play(urgent: boolean): boolean {
    if (!context || !ready()) return false;
    try {
      stopTones();
      const frequencies = urgent ? [520, 784, 1046.5] : [520, 784];
      for (const [index, frequency] of frequencies.entries()) {
        const oscillator = context.createOscillator();
        const gain = context.createGain();
        const startsAt = context.currentTime + index * 0.25;
        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(frequency, startsAt);
        gain.gain.setValueAtTime(0.0001, startsAt);
        gain.gain.exponentialRampToValueAtTime(PEAK_GAIN, startsAt + 0.025);
        gain.gain.exponentialRampToValueAtTime(0.0001, startsAt + 0.22);
        oscillator.connect(gain);
        gain.connect(context.destination);
        const node = { oscillator, gain };
        sounding.add(node);
        oscillator.onended = () => {
          sounding.delete(node);
          try { oscillator.disconnect(); gain.disconnect(); } catch { /* Already disposed. */ }
        };
        oscillator.start(startsAt);
        oscillator.stop(startsAt + 0.22);
      }
      return true;
    } catch {
      stopTones();
      return false;
    }
  }
  function unlock(): Promise<boolean> {
    if (disposed || !activeGesture()) return Promise.resolve(false);
    if (ready()) return Promise.resolve(true);
    if (unlocking) return unlocking;
    const thisGeneration = generation;
    try {
      const AudioContextConstructor = browser?.AudioContext ||
        (browser as (Window & { webkitAudioContext?: typeof AudioContext }) | undefined)?.webkitAudioContext;
      if (!AudioContextConstructor) return Promise.resolve(false);
      if (!context || context.state === 'closed') context = new AudioContextConstructor();
      // Creation and resume are initiated synchronously while the gesture is active.
      const resume = context.state === 'running' ? Promise.resolve() : context.resume();
      const pending = Promise.resolve(resume).then(() => !disposed && generation === thisGeneration && ready(), () => false);
      unlocking = pending;
      void pending.finally(() => { if (unlocking === pending) unlocking = undefined; });
      return pending;
    } catch { return Promise.resolve(false); }
  }
  function remember(values: string[], now: number): void {
    for (const [id, at] of seen) if (now - at > SEEN_TTL_MS || at > now) seen.delete(id);
    for (const id of values) { seen.delete(id); seen.set(id, now); }
    while (seen.size > MAX_SEEN) seen.delete(seen.keys().next().value!);
  }
  function readGuard(now: number): SharedGuard | undefined {
    try {
      const local = storage();
      if (!local) return undefined;
      const raw = local.getItem(guardKey);
      if (!raw) return { lastPlayedAt: 0, seen: [], owner: '' };
      const parsed = JSON.parse(raw) as Partial<SharedGuard>;
      return {
        lastPlayedAt: typeof parsed.lastPlayedAt === 'number' && Number.isFinite(parsed.lastPlayedAt) && parsed.lastPlayedAt <= now ? parsed.lastPlayedAt : 0,
        seen: Array.isArray(parsed.seen) ? parsed.seen.filter((entry): entry is SeenEntry =>
          Array.isArray(entry) && typeof entry[0] === 'string' && /^[a-f0-9]{16}$/.test(entry[0]) &&
          typeof entry[1] === 'number' && Number.isFinite(entry[1]) && entry[1] <= now && now - entry[1] <= SEEN_TTL_MS).slice(-MAX_SEEN) : [],
        owner: typeof parsed.owner === 'string' ? parsed.owner : '',
      };
    } catch { return undefined; }
  }
  function claim(guard: SharedGuard): boolean {
    try {
      const local = storage();
      if (!local) return false;
      local.setItem(guardKey, JSON.stringify(guard));
      return JSON.parse(local.getItem(guardKey) || '{}').owner === owner;
    } catch { return false; }
  }
  function onGesture(event: Event): void {
    if (!disposed && optedIn && !ready() && event.isTrusted !== false) void unlock();
  }
  function onStorage(event: StorageEvent): void {
    if (event.key !== preferenceKey && event.key !== null) return;
    optedIn = readPreference();
    if (!optedIn) { generation++; stopTones(); }
    // Another tab cannot unlock this tab's audio; wait for this tab's next gesture.
  }
  optedIn = readPreference();
  browser?.addEventListener('pointerdown', onGesture, true);
  browser?.addEventListener('keydown', onGesture, true);
  browser?.addEventListener('storage', onStorage);

  return {
    enabled: () => !disposed && optedIn,
    isReady: ready,
    async setEnabled(value) {
      if (disposed) return false;
      if (!value) {
        optedIn = false; generation++; writePreference(); stopTones();
        return true;
      }
      // Programmatic startup cannot grant an opt-in or cause an AudioContext to be created.
      if (!activeGesture()) return false;
      optedIn = true; writePreference();
      return unlock();
    },
    async test() {
      if (disposed || !activeGesture()) return false;
      const thisGeneration = generation;
      return await unlock() && generation === thisGeneration && play(false);
    },
    async notify(ids, urgent) {
      if (disposed) return false;
      const now = Date.now();
      remember([], now);
      const hashes = [...new Set(ids.filter(id => typeof id === 'string' && id.length > 0).map(keyHash))];
      const fresh = hashes.filter(id => !seen.has(id));
      remember(hashes, now);
      // Never resume in polling, enqueue blocked alerts, or play history after an opt-in.
      if (!fresh.length || !optedIn || !ready()) return false;
      const thisGeneration = generation;
      const deliver = (): boolean => {
        if (disposed || thisGeneration !== generation || !optedIn || !ready()) return false;
        const at = Date.now();
        const guard = readGuard(at);
        if (at - lastPlayedAt < COOLDOWN_MS || (guard && at - guard.lastPlayedAt < COOLDOWN_MS)) return false;
        const previous = new Set(guard?.seen.map(entry => entry[0]) || []);
        const unplayed = fresh.filter(id => !previous.has(id));
        if (!unplayed.length) return false;
        if (guard) {
          const next: SharedGuard = {
            lastPlayedAt: at,
            seen: [...guard.seen, ...unplayed.map((id): SeenEntry => [id, at])].slice(-MAX_SEEN), owner,
          };
          // If persistent coordination fails, only the focused visible document may sound.
          if (!claim(next) && !focused()) return false;
        } else if (!focused()) return false;
        if (!play(urgent)) return false;
        lastPlayedAt = at;
        return true;
      };
      try {
        const locks = browser?.navigator.locks;
        if (locks?.request) return await locks.request(lockKey, { mode: 'exclusive' }, deliver);
        // localStorage alone is not an atomic mutex. Only the focused tab may claim without Web Locks.
        return focused() ? deliver() : false;
      } catch { return false; }
    },
    dispose() {
      if (disposed) return;
      disposed = true; generation++; optedIn = false;
      browser?.removeEventListener('pointerdown', onGesture, true);
      browser?.removeEventListener('keydown', onGesture, true);
      browser?.removeEventListener('storage', onStorage);
      stopTones(); seen.clear();
      if (context && context.state !== 'closed') {
        try { void context.close().catch(() => undefined); } catch { /* Browser is already closing. */ }
      }
      context = undefined;
    },
  };
}
